import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Target, Heart, Users, Shield, TrendingUp } from 'lucide-react';
import { FadeIn } from '@/components/Animations';

const MLM_LOGO = "https://customer-assets.emergentagent.com/job_5389659f-dea1-4240-af6c-b14886122f6e/artifacts/6yszwf33_Digital%20Logo%20with%20Framed%20Letters.png";
const GIGFLEET_LOGO = "https://customer-assets.emergentagent.com/job_5389659f-dea1-4240-af6c-b14886122f6e/artifacts/plrdoiq9_Gig%20Fleet%20Logo.png";

const values = [
  {
    icon: Heart,
    title: 'Dignity',
    description: 'Every rider deserves to be treated with respect and dignity, regardless of their employment status.',
  },
  {
    icon: Shield,
    title: 'Safety',
    description: 'Road safety is non-negotiable. We advocate for proper training, equipment, and awareness.',
  },
  {
    icon: TrendingUp,
    title: 'Opportunity',
    description: 'We believe in creating pathways for riders to grow from survival mode to sustainable entrepreneurship.',
  },
  {
    icon: Users,
    title: 'Community',
    description: 'Building a supportive community where riders can learn, grow, and succeed together.',
  },
];

const AboutPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 via-purple-50/30 to-white">
        <div className="container-wide">
          <FadeIn>
            <div className="max-w-4xl">
              <span className="inline-block px-4 py-2 bg-purple-100 text-primary-mlm rounded-full text-sm font-semibold tracking-wide uppercase mb-6">
                About Us
              </span>
              <h1 className="text-4xl md:text-6xl font-heading font-bold text-slate-900 mb-6">
                About <span className="text-primary-mlm">#MyLifeMatters</span>
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed">
                We are a social impact movement dedicated to creating awareness around the lives, 
                safety, and economic dignity of delivery riders working in South Africa's gig economy.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-16">
            <FadeIn>
              <div className="space-y-6">
                <div className="w-16 h-16 bg-primary-mlm rounded-2xl flex items-center justify-center">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900">
                  Our Mission
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  To create awareness around the lives, safety, and economic dignity of delivery 
                  riders working in the gig economy. We promote a new social compact where riders 
                  receive structure, tools, and support to build sustainable businesses.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  This is not charity — it's about creating systemic change in how gig workers 
                  are supported and empowered to succeed.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.2}>
              <div className="space-y-6">
                <div className="w-16 h-16 bg-safety-yellow rounded-2xl flex items-center justify-center">
                  <TrendingUp className="w-8 h-8 text-slate-900" />
                </div>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900">
                  Our Vision
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  A gig economy where every rider has access to safety, training, and a clear 
                  pathway to economic independence. We envision a future where being a delivery 
                  rider is a respected profession with real opportunities for growth.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  We're building a movement that transforms riders from independent contractors 
                  into empowered entrepreneurs — Gigpreneurs.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-wide">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-4 py-2 bg-purple-100 text-primary-mlm rounded-full text-sm font-semibold tracking-wide uppercase mb-6">
                Our Values
              </span>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-slate-900 mb-6">
                What We Stand For
              </h2>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <FadeIn key={value.title} delay={0.1 * index}>
                <div className="bg-white rounded-2xl p-8 border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full">
                  <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                    <value.icon className="w-7 h-7 text-primary-mlm" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600">{value.description}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Strategic Partnership */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container-wide">
          <div className="bg-slate-900 rounded-3xl p-8 md:p-16 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary-mlm/20 rounded-full blur-3xl" />
            
            <div className="grid lg:grid-cols-2 gap-12 items-center relative z-10">
              <FadeIn>
                <div className="space-y-6">
                  <span className="text-slate-400 text-sm font-medium uppercase tracking-wider">Strategic Partner</span>
                  <img src={GIGFLEET_LOGO} alt="Gig Fleet Management" className="h-12 w-auto" />
                  <h2 className="text-3xl md:text-4xl font-heading font-bold text-white">
                    Powered by Gig Fleet Management
                  </h2>
                  <p className="text-slate-300 text-lg leading-relaxed">
                    Gig Fleet Management is the operational engine behind #MyLifeMatters. 
                    They provide the technology platform, training programs, and support 
                    systems that make our mission possible.
                  </p>
                  <p className="text-slate-300 leading-relaxed">
                    Through the Gig Fleet App, riders access daily assignments, training 
                    modules, operational reporting, and business tools that help them 
                    grow from riders to entrepreneurs.
                  </p>
                </div>
              </FadeIn>

              <FadeIn delay={0.2}>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <h3 className="text-xl font-semibold text-white mb-6">What Gig Fleet Provides</h3>
                  <div className="space-y-4">
                    {[
                      'Technology platform for operations',
                      'Structured training programs',
                      'Daily engagement and accountability',
                      'Business tools and reporting',
                      'Growth pathway to ownership',
                    ].map((item) => (
                      <div key={item} className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-safety-yellow rounded-full flex-shrink-0" />
                        <span className="text-white">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-wide">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto">
              <img src={MLM_LOGO} alt="#MyLifeMatters" className="h-16 w-auto mx-auto mb-8" />
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-6">
                Get In Touch
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Want to learn more about #MyLifeMatters or explore partnership opportunities?
              </p>
              <div className="space-y-2 mb-8">
                <p className="text-slate-700"><strong>Andrew Clarke</strong></p>
                <p className="text-slate-600">072 028 6584</p>
                <p className="text-slate-600">ask@gigfleet.co.za</p>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  asChild
                  className="bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full px-8 py-6 font-semibold"
                  data-testid="about-contact-btn"
                >
                  <Link to="/contact">
                    Contact Us
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-2 border-primary-mlm text-primary-mlm hover:bg-primary-mlm hover:text-white rounded-full px-8 py-6 font-semibold"
                  data-testid="about-partner-btn"
                >
                  <Link to="/corporate-csi">
                    Partner With Us
                  </Link>
                </Button>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
